import { useCallback, useEffect, useState, type KeyboardEventHandler, type MouseEventHandler } from 'react';
import { movementAndPlacementToSizeChange, ResizerPlacement } from '../Resizer/useResizer';

const KEYBOARD_RESIZE_STEP = 10;

type UseResizableDrawerWidthOptions = {
  initialWidth: number;
  minWidth: number;
  maxWidth: number;
  placement?: ResizerPlacement;
  enabled?: boolean;
};

function clampWidth(width: number, minWidth: number, maxWidth: number): number {
  return Math.min(maxWidth, Math.max(minWidth, width));
}

export function useResizableDrawerWidth({
  initialWidth,
  minWidth,
  maxWidth,
  placement = ResizerPlacement.LEFT,
  enabled = true,
}: UseResizableDrawerWidthOptions) {
  // A width outside the resize range belongs to another drawer mode. Start at the minimum
  // so that mode does not become the resizable width.
  const [width, setWidth] = useState(() =>
    initialWidth < minWidth || initialWidth > maxWidth ? minWidth : initialWidth
  );
  const [isResizing, setIsResizing] = useState(false);

  const onResizeMouseDown = useCallback<MouseEventHandler<HTMLButtonElement>>(
    event => {
      if (!enabled) {
        return;
      }

      event.preventDefault();
      setIsResizing(true);
    },
    [enabled]
  );

  const onResizeKeyDown = useCallback<KeyboardEventHandler<HTMLButtonElement>>(
    event => {
      if (!enabled || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) {
        return;
      }

      event.preventDefault();
      const movement = event.key === 'ArrowLeft' ? -KEYBOARD_RESIZE_STEP : KEYBOARD_RESIZE_STEP;
      const widthChange = movementAndPlacementToSizeChange(movement, placement);
      setWidth(currentWidth => clampWidth(currentWidth + widthChange, minWidth, maxWidth));
    },
    [enabled, maxWidth, minWidth, placement]
  );

  useEffect(() => {
    if (enabled) {
      setWidth(currentWidth => clampWidth(currentWidth, minWidth, maxWidth));
    }
  }, [enabled, maxWidth, minWidth]);

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      if (event.buttons === 0) {
        setIsResizing(false);
        return;
      }

      const widthChange = movementAndPlacementToSizeChange(event.movementX, placement);
      setWidth(currentWidth => clampWidth(currentWidth + widthChange, minWidth, maxWidth));
    };

    const handleMouseUp = () => setIsResizing(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, maxWidth, minWidth, placement]);

  return {
    width,
    isResizing,
    onResizeMouseDown,
    onResizeKeyDown,
  };
}
